import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';

import { apiSahClaims } from '~constants/api';
import { AuthTags } from '~constants/authTags';
import { DOCUMENT_TYPES, PAGE_SIZE, VALIDATION_POLL_INTERVAL_MS } from '~constants/common';
import { getMissingContent } from '~constants/notFoundMap';

import { getStreamableZipDownload } from '~libs/downloadUtils';
import { download } from '~libs/genericAction';
import { useSearch } from '~libs/searchParams';
import { useToast } from '~libs/toast';

import {
  Button,
  ClickableIcon,
  ContainerHeader,
  DownloadButton,
  ErrorResponse,
  Modal,
  PageNotFound,
  Spinner,
  Toolbar,
} from '~components';
import { SearchBar } from '~components/form-fields/search';
import { faArrowLeft, faMagnifyingGlass, faTrash } from '~components/icon';
import { useDocumentBatchGeneration } from '~main/components/document-batch-jobs/useDocumentBatchGeneration';

import searchFields from '~support-at-home/forms/claims/claimItemsSearch';

import { setTitle } from '~main/actions/login';
import {
  claimsApi,
  useDeleteClaimMutation,
  useFinaliseClaimMutation,
  useGetClaimItemsQuery,
  useGetClaimQuery,
  useUnfinaliseClaimMutation,
} from '~support-at-home/actions/claims';

import { selectUserTags } from '~main/selectors/login';

import ClaimDetailsEdit from './ClaimDetailsEdit';

const Claim: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const tags = useAppSelector(selectUserTags);
  const canUnfinalise = tags.includes(AuthTags.SUPPORTATHOME.CLAIMS.ADMIN);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [debouncedPage, setDebouncedPage] = useState(1);
  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearch('claim-items-search', {});
  const search = (values: { [key: string]: any }) => setSearchParams(values);
  const pageDebounceTimer = useRef<ReturnType<typeof setTimeout>>();

  const dispatch = useAppDispatch();

  const { generateSeparate, isGenerating } = useDocumentBatchGeneration();
  const { showToast } = useToast();

  const [deleteClaim, { isLoading: isDeleting, error: deleteError, reset: resetDeleteClaim }] =
    useDeleteClaimMutation();
  const [finaliseClaim, { isLoading: finaliseLoading, error: finaliseError, reset: resetFinaliseClaim }] =
    useFinaliseClaimMutation();
  const [unfinaliseClaim, { isLoading: unfinaliseLoading, error: unfinaliseError, reset: resetUnfinaliseClaim }] =
    useUnfinaliseClaimMutation();

  const claimQueryArgs = useMemo<{ id: string } | typeof skipToken>(() => {
    if (!id) return skipToken;
    return { id };
  }, [id]);

  const itemsQueryArgs = useMemo<
    ({ id: string; limit?: number; offset?: number } & Record<string, any>) | typeof skipToken
  >(() => {
    if (!id) return skipToken;
    const offset = (debouncedPage - 1) * PAGE_SIZE;
    return { id, limit: PAGE_SIZE, offset, ...searchParams };
  }, [id, debouncedPage, searchParams]);

  const {
    data: claim,
    isLoading: claimLoading,
    isUninitialized: claimUninitialized,
    refetch: refetchClaim,
  } = useGetClaimQuery(claimQueryArgs, {
    refetchOnMountOrArgChange: true,
  });

  const { data: claimItems, isFetching: itemsLoading } = useGetClaimItemsQuery(itemsQueryArgs, {
    refetchOnMountOrArgChange: true,
  });

  const isLoading = claimLoading;

  useEffect(() => {
    if (claim) {
      setTitle(`Claim - ${claim.reference}`);
    } else {
      setTitle('Claim Details');
    }
  }, [claim]);

  useEffect(() => {
    // Debounce to prevent rapid API calls when changing
    if (pageDebounceTimer.current) {
      clearTimeout(pageDebounceTimer.current);
    }
    pageDebounceTimer.current = setTimeout(() => {
      setDebouncedPage(currentPage);
    }, 150);

    return () => {
      if (pageDebounceTimer.current) {
        clearTimeout(pageDebounceTimer.current);
      }
    };
  }, [currentPage]);

  useEffect(() => {
    return () => {
      resetDeleteClaim();
      resetFinaliseClaim();
      resetUnfinaliseClaim();
    };
  }, []);

  // Poll for validation completion while is_validating is true
  const prevIsValidatingRef = useRef(claim?.is_validating);
  useEffect(() => {
    // Detect transition from validating → done and refresh claim items
    if (prevIsValidatingRef.current && !claim?.is_validating && id) {
      dispatch(claimsApi.util.invalidateTags([{ type: 'claim-items', id: `CLAIM_${id}` }]));
    }
    prevIsValidatingRef.current = claim?.is_validating;

    if (!claim?.is_validating) return;

    const interval = setInterval(() => {
      refetchClaim();
    }, VALIDATION_POLL_INTERVAL_MS);

    return () => {
      clearInterval(interval);
    };
  }, [claim?.is_validating, refetchClaim, dispatch, id]);

  const onDelete = async () => {
    if (!id) {
      setDeleteModalOpen(false);
      return;
    }

    try {
      const { data: payload } = await deleteClaim({ id });
      if (payload?.success) {
        navigate('/support-at-home/claims');
      }
    } finally {
      setDeleteModalOpen(false);
    }
  };

  const onFinalise = async () => {
    if (!id) return;
    const { data: payload } = await finaliseClaim({ id });
    if (payload?.success) {
      // Refetch will happen automatically via RTK Query
    }
  };

  const onUnfinalise = async () => {
    if (!id) return;
    const { data: payload } = await unfinaliseClaim({ id });
    if (payload?.success) {
      // Refetch will happen automatically via RTK Query
    }
  };

  const onGenerateCsv = async () => {
    if (!id) return;
    try {
      await generateSeparate(DOCUMENT_TYPES.SAH_CLAIM.CSV, [id]).unwrap();
      showToast('success', 'CSV generation started');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to start CSV generation';
      showToast('danger', message);
    }
  };

  const onDownload = async (updateProgressValue?: (value: string) => void) => {
    if (!id) return null;
    try {
      const data = await download(`${apiSahClaims}/${id}/export-aco`, {}, (progress) => {
        updateProgressValue?.(typeof progress === 'number' ? `${progress}%` : String(progress));
      });
      const reference = claim?.reference || id;
      const sanitizedFilename = reference.replace(/[/\\:*?"<>|]/g, '_').trim();
      return getStreamableZipDownload({ blob: data.data, filename: `${sanitizedFilename}.zip` });
    } catch (err) {
      console.error('Export failed:', err);
      return null;
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRefreshClaim = () => {
    if (!claimUninitialized) {
      refetchClaim();
    }
  };

  const isFinalised = claim?.status.tag === 'finalised';
  const hasStatementItems = claim?.items?.some((item) => item.statement?.id) ?? false;
  const deleteErr = deleteError as any;
  const finaliseErr = finaliseError as any;
  const unfinaliseErr = unfinaliseError as any;
  const errors = [deleteErr?.data?.message, finaliseErr?.data?.message, unfinaliseErr?.data?.message].filter(Boolean);

  const getHeaderTitle = () => {
    if (isLoading) {
      return 'Loading...';
    }
    return claim ? claim.reference : 'Not Found';
  };

  return (
    <div className="p-2">
      <Modal
        isOpen={deleteModalOpen}
        setIsOpen={setDeleteModalOpen}
        headerClassName="bg-warning"
        header="Warning"
        includeFooter={true}
        footer={
          <div className="d-flex gap-2">
            <Button size="sm" className="ps-4 pe-4" color="danger" loading={isDeleting} onClick={onDelete}>
              Delete
            </Button>
            <Button
              size="sm"
              className="ps-4 pe-4"
              data-cy="sah-claim-delete-cancel-button"
              onClick={() => setDeleteModalOpen(false)}
            >
              Cancel
            </Button>
          </div>
        }
      >
        <div className="mb-4">Are you sure you want to delete claim?</div>
        <div>This action will fail if claim has been finalised.</div>
        <div className="fw-bold mt-4">This is an irreversible action</div>
      </Modal>

      <ContainerHeader dataCy="claim-header" className="info">
        <span>Claim Details - {getHeaderTitle()}</span>
      </ContainerHeader>

      <Toolbar
        id="claim-details-toolbar"
        leftCluster={
          <ClickableIcon
            icon={faArrowLeft}
            id="claim-back-button"
            onClick={() => navigate('/support-at-home/claims')}
            toolTip="Back to Claims"
          />
        }
        rightCluster={
          <>
            <ClickableIcon
              id="claim-items-search-button"
              icon={faMagnifyingGlass}
              onClick={() => setSearchBarOpen(!searchBarOpen)}
              toolTip="Toggle Search Bar"
            />
            <ClickableIcon
              id="sah-claim-delete-icon"
              icon={faTrash}
              className="mx-2"
              color="danger"
              disabled={isFinalised || hasStatementItems}
              showTooltipWhenDisabled={hasStatementItems}
              onClick={() => setDeleteModalOpen(true)}
              toolTip={hasStatementItems ? 'Cannot delete - claim has items included in statements' : 'Delete claim'}
            />
            <DownloadButton
              caption="Export ACO Upload"
              classNames="ps-4 pe-4"
              handleDownload={onDownload}
              disabled={!isFinalised}
              size="sm"
              color="secondary"
            />
            <Button size="sm" className="ps-4 pe-4" color="secondary" loading={isGenerating} disabled={isGenerating} onClick={onGenerateCsv}>
              Generate CSV
            </Button>
            {(isFinalised || hasStatementItems) && canUnfinalise ? (
              <Button size="sm" className="ps-4 pe-4" color="danger" loading={unfinaliseLoading} onClick={onUnfinalise}>
                Unfinalise
              </Button>
            ) : (
              <Button
                size="sm"
                className="ps-4 pe-4"
                color="success"
                disabled={(claim?.items_with_errors ?? 0) > 0 || isFinalised || hasStatementItems}
                loading={finaliseLoading}
                onClick={onFinalise}
              >
                Finalise
              </Button>
            )}
          </>
        }
      />

      <ErrorResponse message={errors.join(',')} dataCy="claim-errorResponse" />
      <SearchBar isOpen={searchBarOpen} fields={searchFields} onSubmit={search} />
      {isLoading ? (
        <div className="d-flex justify-content-center p-5">
          <Spinner loading />
        </div>
      ) : (
        <>
          {claim ? (
            <div className="p-2">
              <ClaimDetailsEdit
                claim={claim}
                claimItems={claimItems}
                itemsLoading={itemsLoading}
                currentPage={currentPage}
                pageSize={PAGE_SIZE}
                onPageChange={handlePageChange}
                onRefreshClaim={handleRefreshClaim}
              />
            </div>
          ) : (
            <PageNotFound content={getMissingContent('claim')} />
          )}
        </>
      )}
    </div>
  );
};

export default Claim;
